import React, { useCallback, useEffect, useState } from 'react'
import { string } from 'yup'
import PropTypes from 'prop-types'
import { Formik, useFormikContext } from 'formik'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'
import { ABSpinner, useErrorService } from '@abroad/components'
import { ExperienceSelect } from '../form'
import ExperienceDatePicker from '../form/ExperienceDatePicker'
import {
  formatDate,
  getCurrentDate,
  getUTCcurrentDateUsingProps,
  UTCformatDateInPlan,
} from '../../utils/date'
import API from '../../utils/API'

const questNameValidationSchema = string().required(
  'Experience name is required',
)

const CopyQuestForm = ({
  questName,
  questNameError,
  handleQuestNameChange,
  handleCreateClick,
  handleClose,
  renderItineraryMapping,
  isLoading,
  generateNewDateSequence,
}) => {
  const { values } = useFormikContext()

  useEffect(() => {
    if (values.questStartDate && values.questEndDate) {
      generateNewDateSequence(values.questStartDate, values.questEndDate)
    }
  }, [values.questStartDate, values.questEndDate, generateNewDateSequence])

  return (
    <>
      <label className='experience-f-label' htmlFor='copyQuestNameField'>
        New experience name
      </label>
      <input
        id='copyQuestNameField'
        type='text'
        className='experience-f-control'
        value={questName}
        onChange={(e) => handleQuestNameChange(e.target.value)}
        placeholder='Enter new experience name…'
        disabled={isLoading}
        autoComplete='off'
      />
      {questNameError ? (
        <p className='experience-f-error' role='alert'>
          {questNameError}
        </p>
      ) : null}

      <div className='mt-3'>
        <ExperienceDatePicker
          name='questStartDate'
          label='Experience starts'
          labelProps={{ className: 'experience-f-label' }}
          className='experience-f-control text-capitalize'
          minimumDate={getCurrentDate()}
          maximumDate={
            values.questEndDate
              ? getUTCcurrentDateUsingProps(
                  UTCformatDateInPlan(values.questEndDate),
                )
              : getCurrentDate().add(10, 'years')
          }
          rangeStart={values.questStartDate}
          rangeEnd={values.questEndDate}
          key={
            values.questEndDate
              ? getUTCcurrentDateUsingProps(
                  UTCformatDateInPlan(values.questEndDate),
                )
              : 'no-end-date'
          }
          displayFlex={false}
          disabled={isLoading}
        />
      </div>

      <div className='mt-3'>
        <ExperienceDatePicker
          name='questEndDate'
          label='Experience ends'
          labelProps={{ className: 'experience-f-label' }}
          className='experience-f-control text-capitalize'
          minimumDate={
            values.questStartDate
              ? getUTCcurrentDateUsingProps(
                  UTCformatDateInPlan(values.questStartDate),
                )
              : getCurrentDate()
          }
          maximumDate={
            values.questStartDate
              ? getUTCcurrentDateUsingProps(
                  UTCformatDateInPlan(values.questStartDate),
                ).add(10, 'years')
              : getCurrentDate().add(10, 'years')
          }
          rangeStart={values.questStartDate}
          rangeEnd={values.questEndDate}
          key={getUTCcurrentDateUsingProps(
            UTCformatDateInPlan(values.questStartDate),
          )}
          displayFlex={false}
          disabled={isLoading}
        />
      </div>

      {renderItineraryMapping()}

      <div className='experience-copy-modal-actions'>
        <button
          type='button'
          className='btn-outline-experience'
          onClick={handleClose}
          disabled={isLoading}>
          Cancel
        </button>
        <button
          type='button'
          className='btn-navy'
          disabled={
            isLoading || questName.trim() === '' || questNameError !== ''
          }
          onClick={() => handleCreateClick(values)}>
          {isLoading ? 'Creating…' : 'Create experience'}
        </button>
      </div>
    </>
  )
}

CopyQuestForm.propTypes = {
  questName: PropTypes.string.isRequired,
  questNameError: PropTypes.string.isRequired,
  handleQuestNameChange: PropTypes.func.isRequired,
  handleCreateClick: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  renderItineraryMapping: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  generateNewDateSequence: PropTypes.func.isRequired,
}

const CopyQuestModal = ({
  show,
  onHide,
  selectedQuest,
  onConfirmCopy,
  isLoading = false,
}) => {
  const [questName, setQuestName] = useState('')
  const [questNameError, setQuestNameError] = useState('')
  const [oldItineraryDates, setOldItineraryDates] = useState([])
  const [oldItineraryData, setOldItineraryData] = useState([])
  const [newItineraryDates, setNewItineraryDates] = useState([])
  const [itineraryMapping, setItineraryMapping] = useState([])
  const [isLoadingItinerary, setIsLoadingItinerary] = useState(false)
  const Error = useErrorService()
  const sourceLabel =
    selectedQuest?.label ||
    selectedQuest?.extra?.title ||
    selectedQuest?.extra?.name ||
    ''

  useEffect(() => {
    if (selectedQuest?.label) {
      setQuestName(`Copy of ${selectedQuest.label}`)
      setQuestNameError('')
    }
  }, [selectedQuest])

  useEffect(() => {
    const fetchItineraryDates = async () => {
      if (!selectedQuest?.value || !show) {
        return
      }
      try {
        setIsLoadingItinerary(true)
        const { data } = await API.AdminAPI.quests.getItinerary(
          selectedQuest.value,
        )

        if (data?.days && Array.isArray(data.days)) {
          const dates = data.days
            .map((day) => day.date)
            .filter((date) => date)
            .map((date) => formatDate(moment(date)))

          setOldItineraryDates(dates)
          setOldItineraryData(data.days)
        } else {
          setOldItineraryDates([])
          setOldItineraryData([])
        }
      } catch (e) {
        Error.showError(e)
        setOldItineraryDates([])
        setOldItineraryData([])
      } finally {
        setIsLoadingItinerary(false)
      }
    }

    fetchItineraryDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuest?.value, show])

  const generateNewDateSequence = useCallback(
    (startDate, endDate) => {
      if (!startDate || !endDate) {
        setNewItineraryDates([])
        setItineraryMapping([])
        return
      }

      const start = moment(startDate, 'MM/DD/YYYY')
      const end = moment(endDate, 'MM/DD/YYYY')

      if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
        setNewItineraryDates([])
        setItineraryMapping([])
        return
      }

      const newDates = []
      const current = start.clone()

      while (current.isSameOrBefore(end)) {
        newDates.push(formatDate(current))
        current.add(1, 'day')
      }

      setNewItineraryDates(newDates)

      const oldCount = oldItineraryDates.length
      const mappingRows = newDates.map((newDate, index) => ({
        id: index,
        oldDate: index < oldCount ? oldItineraryDates[index] : 'Keep Empty',
        newDate,
      }))

      setItineraryMapping(mappingRows)
    },
    [oldItineraryDates],
  )

  const validateQuestName = async (name) => {
    try {
      await questNameValidationSchema.validate(name)
      setQuestNameError('')
      return true
    } catch (err) {
      setQuestNameError(err.message)
      return false
    }
  }

  const handleQuestNameChange = async (value) => {
    setQuestName(value)
    if (value.trim()) {
      await validateQuestName(value.trim())
    } else {
      setQuestNameError('Experience name is required')
    }
  }

  const handleCreateClick = async (formValues) => {
    const trimmedName = questName.trim()
    const isValidName = await validateQuestName(trimmedName)
    if (!isValidName) {
      return
    }

    const itinerary = itineraryMapping.map((mapping) => {
      if (mapping.oldDate === 'Keep Empty') {
        return {
          date: UTCformatDateInPlan(mapping.newDate),
          subTitle: '',
          imageFileName: '',
          itineraries: [],
        }
      }

      const originalDay = oldItineraryData.find(
        (day) => formatDate(moment(day.date)) === mapping.oldDate,
      )

      return {
        date: UTCformatDateInPlan(mapping.newDate),
        subTitle: originalDay?.subTitle || '',
        imageFileName: originalDay?.imageFileName || '',
        itineraries: (originalDay?.itineraries || []).map(
          ({ _id, ...itineraryItem }) => itineraryItem,
        ),
      }
    })

    if (onConfirmCopy && selectedQuest) {
      await onConfirmCopy(
        selectedQuest.value,
        selectedQuest.extra,
        trimmedName,
        {
          ...formValues,
          itineraryMapping,
          itinerary,
        },
      )
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setQuestName('')
      setQuestNameError('')
      setOldItineraryDates([])
      setOldItineraryData([])
      setNewItineraryDates([])
      setItineraryMapping([])
      onHide()
    }
  }

  const handleOldDateChange = (index, value) => {
    const updatedMapping = [...itineraryMapping]
    updatedMapping[index].oldDate = value
    setItineraryMapping(updatedMapping)
  }

  const getAvailableDatesForDropdown = (currentIndex) => {
    const selectedDates = itineraryMapping
      .map((mapping, idx) => (idx !== currentIndex ? mapping.oldDate : null))
      .filter((date) => date && date !== 'Keep Empty')

    return oldItineraryDates.filter((date) => !selectedDates.includes(date))
  }

  const oldDateOptions = (index) => [
    { value: 'Keep Empty', label: 'Keep Empty' },
    ...getAvailableDatesForDropdown(index).map((date) => ({
      value: date,
      label: date,
    })),
  ]

  const renderItineraryMapping = () => {
    if (!newItineraryDates.length) {
      return (
        <p className='experience-copy-modal-lead mt-3 mb-0'>
          Choose start and end dates to map itinerary days from the source
          experience.
        </p>
      )
    }

    return (
      <div className='mt-4'>
        <div className='d-flex align-items-center mb-3'>
          <h4 className='experience-field-head-title mb-0'>
            Itinerary mapping
          </h4>
          {selectedQuest?.value ? (
            <Link
              to={`/admin/experiences/${selectedQuest.value}/itinerary`}
              target='_blank'
              rel='noopener noreferrer'
              className='experience-linkbtn ml-2'>
              (See original)
            </Link>
          ) : null}
        </div>

        {isLoadingItinerary ? (
          <div className='text-center py-3'>
            <ABSpinner size='sm' />
          </div>
        ) : (
          <>
            <div className='experience-map-head'>
              <span>Old dates</span>
              <span aria-hidden='true' />
              <span>New dates</span>
            </div>
            <div className='experience-map-rows'>
              {itineraryMapping.map((mapping, index) => (
                <div key={mapping.id} className='experience-map-row'>
                  <ExperienceSelect
                    value={mapping.oldDate}
                    options={oldDateOptions(index)}
                    isSearchable={false}
                    disabled={isLoading}
                    onChange={(e) => handleOldDateChange(index, e.target.value)}
                  />
                  <span className='experience-map-arrow' aria-hidden='true'>
                    →
                  </span>
                  <input
                    type='text'
                    className='experience-f-control'
                    value={mapping.newDate}
                    disabled
                    readOnly
                    tabIndex={-1}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose()
    }
  }

  if (!show) {
    return null
  }

  return (
    <div
      className='experience-copy-modal-backdrop open'
      role='dialog'
      aria-modal='true'
      aria-labelledby='copyQuestModalTitle'
      onClick={handleBackdropClick}>
      <div className='experience-copy-modal-card experience-remap-card'>
        <div className='experience-copy-modal-scroll'>
          <h2 id='copyQuestModalTitle'>
            {sourceLabel ? `Copy from ${sourceLabel}?` : 'Copy experience'}
          </h2>
          <p className='experience-copy-modal-lead'>
            Set the new name and programme dates. Map source itinerary days onto
            the new date range, or leave days empty.
          </p>

          <Formik
            initialValues={{
              questStartDate: '',
              questEndDate: '',
            }}
            enableReinitialize={false}
            onSubmit={() => {}}>
            <CopyQuestForm
              questName={questName}
              questNameError={questNameError}
              handleQuestNameChange={handleQuestNameChange}
              handleCreateClick={handleCreateClick}
              handleClose={handleClose}
              renderItineraryMapping={renderItineraryMapping}
              isLoading={isLoading}
              generateNewDateSequence={generateNewDateSequence}
            />
          </Formik>
        </div>
      </div>
    </div>
  )
}

CopyQuestModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedQuest: PropTypes.object,
  onConfirmCopy: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

export default CopyQuestModal
